import { ReconciliationRepository } from "../ReconciliationRepository";
import {
  ReconciliationSessionItemModel,
  PageableModel,
  GetListReconciliationSessionsParams,
  ReconciliationSessionDetailModel,
  ReconciliationTransactionModel,
  GetReconciliationSessionTransactionsParams,
  ExportReconciliationSessionsParams,
  ExportReconciliationSessionTransactionsParams,
} from "@/models/ReconciliationModel";
import { ErrorModel } from "@/models/ErrorModel";
import { KienlongPayClient } from "@/api/kienlong_pay_http_client";
import {
  mapReconciliationSessionsListResponse,
  mapReconciliationSessionDetailModel,
  mapReconciliationTransactionsListResponse,
} from "@/adapters/reconciliation_adapter";

/**
 * Implementation của ReconciliationRepository sử dụng PORTALReconciliationControllerApi
 *
 * API endpoints:
 * - GET /api/reconciliation/portal/v1/sessions
 * - GET /api/reconciliation/portal/v1/sessions/{id}
 * - GET /api/reconciliation/portal/v1/sessions/{sessionId}/transactions
 * - GET /api/reconciliation/portal/v1/sessions/export
 * - GET /api/reconciliation/portal/v1/sessions/{sessionId}/transactions/export
 */
export class ReconciliationRepositoryImp implements ReconciliationRepository {
  private client: KienlongPayClient;

  constructor(kienlongPayClient: KienlongPayClient) {
    this.client = kienlongPayClient;
  }

  /**
   * GET /api/reconciliation/portal/v1/sessions
   */
  public async getListReconciliationSessions(
    params: GetListReconciliationSessionsParams,
  ): Promise<PageableModel<ReconciliationSessionItemModel>> {
    try {
      const resp =
        await this.client.portalReconciliationController.getReconciliationSessions(
          {
            fromDate: params.fromDate,
            toDate: params.toDate,
            channels:
              params.channels && params.channels.length > 0
                ? (params.channels as "TCB"[])
                : undefined,
            statuses:
              params.statuses && params.statuses.length > 0
                ? params.statuses.map(
                    (s) => s as "WAITING" | "SOLVING" | "SUCCESS" | "FAILED",
                  )
                : undefined,
            page: params.page,
            size: params.size,
            sort: params.sort,
          },
        );

      if (resp.data.code === 0 && resp.data.data) {
        return mapReconciliationSessionsListResponse(resp.data.data);
      } else {
        throw new ErrorModel({
          code: resp.data.code || 500,
          message: resp.data.message || "Lỗi khi lấy danh sách phiên đối soát",
        });
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * GET /api/reconciliation/portal/v1/sessions/{id}
   */
  public async getReconciliationSessionDetail(params: {
    id: string;
  }): Promise<ReconciliationSessionDetailModel> {
    try {
      const resp =
        await this.client.portalReconciliationController.getReconciliationSession(
          {
            id: params.id,
          },
        );

      if (resp.data.code === 0 && resp.data.data) {
        return mapReconciliationSessionDetailModel(resp.data.data);
      } else {
        throw new ErrorModel({
          code: resp.data.code || 404,
          message:
            resp.data.message ||
            `Không tìm thấy phiên đối soát với ID: ${params.id}`,
        });
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * GET /api/reconciliation/portal/v1/sessions/{sessionId}/transactions
   */
  public async getReconciliationSessionTransactions(
    params: GetReconciliationSessionTransactionsParams,
  ): Promise<PageableModel<ReconciliationTransactionModel>> {
    try {
      const resp =
        await this.client.portalReconciliationController.getReconciliationSessionTransactions(
          {
            sessionId: params.sessionId,
            equal: params.equal,
            page: params.page,
            size: params.size,
            sort: params.sort,
          },
        );

      if (resp.data.code === 0 && resp.data.data) {
        return mapReconciliationTransactionsListResponse(resp.data.data);
      } else {
        throw new ErrorModel({
          code: resp.data.code || 500,
          message:
            resp.data.message ||
            `Lỗi khi lấy giao dịch của phiên đối soát ${params.sessionId}`,
        });
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * GET /api/reconciliation/portal/v1/sessions/export
   */
  public async exportReconciliationSessions(
    params: ExportReconciliationSessionsParams,
  ): Promise<Blob> {
    try {
      const resp =
        await this.client.portalReconciliationController.exportReconciliationSessions(
          {
            fromDate: params.fromDate,
            toDate: params.toDate,
            channels:
              params.channels && params.channels.length > 0
                ? (params.channels as "TCB"[])
                : undefined,
            statuses:
              params.statuses && params.statuses.length > 0
                ? params.statuses.map(
                    (s) => s as "WAITING" | "SOLVING" | "SUCCESS" | "FAILED",
                  )
                : undefined,
          },
          {
            responseType: "blob", // Important: specify blob response type for file download
          },
        );

      if (resp.status === 200 && resp.data) {
        return resp.data;
      } else {
        throw new ErrorModel({
          code: resp.status || 500,
          message: resp.statusText || "Lỗi khi xuất danh sách phiên đối soát",
        });
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * GET /api/reconciliation/portal/v1/sessions/{sessionId}/transactions/export
   */
  public async exportReconciliationSessionTransactions(
    params: ExportReconciliationSessionTransactionsParams,
  ): Promise<Blob> {
    try {
      const resp =
        await this.client.portalReconciliationController.exportReconciliationSessionTransactions(
          {
            sessionId: params.sessionId,
            equal: params.equal,
          },
          {
            responseType: "blob", // Important: specify blob response type for file download
          },
        );

      if (resp.status === 200 && resp.data) {
        return resp.data as Blob;
      } else {
        throw new ErrorModel({
          code: resp.status || 500,
          message:
            resp.statusText ||
            `Lỗi khi xuất giao dịch của phiên đối soát ${params.sessionId}`,
        });
      }
    } catch (error) {
      throw error;
    }
  }
}
